/**
 * STATUS REGISTRY -- Label & Color cho UI
 *
 * Single Source of Truth cho label tiếng Việt và Tailwind color classes.
 * Frontend import trực tiếp, không cần duplicate.
 */

// ── Types ──

export interface StatusConfig {
  label: string;
  color: string;
  dotColor?: string;
  icon?: string;
}

export type StatusMap<T extends string = string> = Record<T, StatusConfig>;

// ══════════════════════════════════════════════
// SYSTEM MODULE
// ══════════════════════════════════════════════

export const UserStatusRegistry: StatusMap = {
  ACTIVE: { label: 'Hoạt động', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  INACTIVE: { label: 'Ngừng HĐ', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  SUSPENDED: { label: 'Tạm khóa', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const NotificationTypeRegistry: StatusMap = {
  INFO: { label: 'Thông tin', color: 'bg-blue-100 text-blue-700' },
  WARNING: { label: 'Cảnh báo', color: 'bg-amber-100 text-amber-700' },
  ERROR: { label: 'Lỗi', color: 'bg-rose-100 text-rose-700' },
  SUCCESS: { label: 'Thành công', color: 'bg-emerald-100 text-emerald-700' },
};

export const MachineTypeRegistry: StatusMap = {
  OFFSET_PRINTER: { label: 'Máy in Offset', color: 'bg-indigo-100 text-indigo-700' },
  FLEXO_PRINTER: { label: 'Máy in Flexo', color: 'bg-cyan-100 text-cyan-700' },
  PAPER_CUTTER: { label: 'Máy xén giấy', color: 'bg-slate-100 text-slate-700' },
  COATER: { label: 'Máy tráng phủ', color: 'bg-amber-100 text-amber-700' },
  SILK_SCREEN: { label: 'Máy in lụa', color: 'bg-purple-100 text-purple-700' },
  LAMINATOR: { label: 'Máy cán màng', color: 'bg-teal-100 text-teal-700' },
  HOT_FOIL: { label: 'Máy ép kim', color: 'bg-yellow-100 text-yellow-700' },
  DIE_CUTTER: { label: 'Máy bế', color: 'bg-orange-100 text-orange-700' },
  WINDOW_PATCHER: { label: 'Máy dán cửa sổ', color: 'bg-lime-100 text-lime-700' },
  FOLDER_GLUER: { label: 'Máy gấp dán', color: 'bg-emerald-100 text-emerald-700' },
  PACKING: { label: 'Máy đóng gói', color: 'bg-blue-100 text-blue-700' },
  OTHER: { label: 'Khác', color: 'bg-slate-100 text-slate-600' },
};

export const MachineStatusRegistry: StatusMap = {
  IDLE: { label: 'Chờ việc', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  RUNNING: { label: 'Đang chạy', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  MAINTENANCE: { label: 'Bảo trì', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  DOWN: { label: 'Hỏng máy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const MachineLogTypeRegistry: StatusMap = {
  OPERATION: { label: 'Vận hành', color: 'bg-emerald-100 text-emerald-700' },
  DOWNTIME: { label: 'Dừng máy', color: 'bg-rose-100 text-rose-700' },
  MAINTENANCE: { label: 'Bảo trì', color: 'bg-amber-100 text-amber-700' },
  SETUP: { label: 'Cài đặt', color: 'bg-blue-100 text-blue-700' },
};

export const ProcessCategoryRegistry: StatusMap = {
  TRUOC_IN: { label: 'Trước in', color: 'bg-purple-50 text-purple-700', dotColor: 'bg-purple-500' },
  IN: { label: 'In ấn', color: 'bg-blue-50 text-blue-700', dotColor: 'bg-blue-500' },
  SAU_IN: { label: 'Sau in', color: 'bg-amber-50 text-amber-700', dotColor: 'bg-amber-500' },
  THANH_PHAM: { label: 'Thành phẩm', color: 'bg-emerald-50 text-emerald-700', dotColor: 'bg-emerald-500' },
  KHAC: { label: 'Khác', color: 'bg-slate-50 text-slate-700', dotColor: 'bg-slate-400' },
};

// ══════════════════════════════════════════════
// MASTER DATA MODULE
// ══════════════════════════════════════════════

export const MaterialCategoryRegistry: StatusMap = {
  PAPER: { label: 'Giấy', color: 'bg-amber-100 text-amber-700' },
  INK: { label: 'Mực in', color: 'bg-indigo-100 text-indigo-700' },
  GLUE: { label: 'Keo dán', color: 'bg-orange-100 text-orange-700' },
  FILM: { label: 'Màng', color: 'bg-cyan-100 text-cyan-700' },
  CHEMICAL: { label: 'Hóa chất', color: 'bg-purple-100 text-purple-700' },
  OTHER: { label: 'Khác', color: 'bg-slate-100 text-slate-600' },
};

// ══════════════════════════════════════════════
// SALES MODULE
// ══════════════════════════════════════════════

export const QuotationStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  SENT: { label: 'Đã gửi', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  PROCESSING: { label: 'Đang xử lý', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  ESTIMATED: { label: 'Đã tính giá', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
  APPROVED: { label: 'Đã duyệt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  REJECTED: { label: 'Từ chối', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const OrderStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  PENDING: { label: 'Chờ duyệt', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  WAITING_PRE_PRESS: { label: 'Chờ bình bài', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
  CONFIRMED: { label: 'Đã xác nhận', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  APPROVED: { label: 'Đã duyệt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  PLANNED: { label: 'Đã lên KH', color: 'bg-indigo-50 text-indigo-700', dotColor: 'bg-indigo-400' },
  IN_PROGRESS: { label: 'Đang SX (legacy)', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
  IN_PRODUCTION: { label: 'Đang SX', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
  COMPLETED: { label: 'Hoàn thành', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  DELIVERED: { label: 'Đã giao', color: 'bg-teal-100 text-teal-700', dotColor: 'bg-teal-500' },
  INVOICED: { label: 'Đã xuất HĐ', color: 'bg-purple-100 text-purple-700', dotColor: 'bg-purple-500' },
  CLOSED: { label: 'Đã đóng', color: 'bg-slate-100 text-slate-700', dotColor: 'bg-slate-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const OrderDetailStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ xử lý', color: 'bg-slate-100 text-slate-700', dotColor: 'bg-slate-400' },
  WAITING_PREPRESS: { label: 'Chờ bình file', color: 'bg-amber-100 text-amber-800', dotColor: 'bg-amber-500' },
  READY_FOR_PREPRESS: { label: 'Chờ bình file', color: 'bg-amber-100 text-amber-800', dotColor: 'bg-amber-500' },
  PREPRESS_ASSIGNED: { label: 'Chờ tạo lệnh', color: 'bg-emerald-100 text-emerald-800', dotColor: 'bg-emerald-500' },
  PREPRESS_PLANNED: { label: 'Chờ tạo lệnh', color: 'bg-emerald-100 text-emerald-800', dotColor: 'bg-emerald-500' },
  PO_CREATED: { label: 'Đã tạo lệnh', color: 'bg-sky-100 text-sky-800', dotColor: 'bg-sky-500' },
  RE_PREPRESS: { label: 'Yêu cầu bình lại', color: 'bg-rose-100 text-rose-800', dotColor: 'bg-rose-500' },
  IN_PRODUCTION: { label: 'Đang SX', color: 'bg-indigo-100 text-indigo-800', dotColor: 'bg-indigo-500' },
  PACKAGED: { label: 'Đã đóng gói', color: 'bg-cyan-100 text-cyan-800', dotColor: 'bg-cyan-500' },
  COMPLETED: { label: 'Hoàn thành', color: 'bg-emerald-500 text-white', dotColor: 'bg-emerald-200' },
  DELIVERED: { label: 'Đã giao đủ', color: 'bg-teal-700 text-white', dotColor: 'bg-teal-200' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-slate-200 text-slate-600', dotColor: 'bg-slate-400' },
};

export const OrderTypeRegistry: StatusMap = {
  PRODUCTION: { label: 'Sản xuất', color: 'bg-blue-100 text-blue-700' },
  SAMPLE: { label: 'Đơn mẫu', color: 'bg-purple-100 text-purple-700' },
  REPLACEMENT: { label: 'Bù hàng', color: 'bg-amber-100 text-amber-700' },
};

export const SampleTypeRegistry: StatusMap = {
  INTERNAL_FREE: { label: 'Nội bộ (miễn phí)', color: 'bg-slate-100 text-slate-600' },
  INTERNAL_CHARGED: { label: 'Nội bộ (tính phí)', color: 'bg-blue-100 text-blue-700' },
  EXTERNAL: { label: 'Khách hàng', color: 'bg-emerald-100 text-emerald-700' },
};

// ══════════════════════════════════════════════
// PRODUCTION MODULE
// ══════════════════════════════════════════════

export const ProductionPriorityRegistry: StatusMap = {
  LOW: { label: 'Thấp', color: 'bg-slate-100 text-slate-600' },
  NORMAL: { label: 'Bình thường', color: 'bg-blue-100 text-blue-700' },
  HIGH: { label: 'Ưu tiên cao', color: 'bg-amber-100 text-amber-700' },
  URGENT: { label: 'Hỏa tốc', color: 'bg-rose-100 text-rose-700' },
};

export const OrderPriorityRegistry = ProductionPriorityRegistry;

export const ProductionOrderStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  PRE_PRESS: { label: 'Chế bản', color: 'bg-purple-100 text-purple-700', dotColor: 'bg-purple-500' },
  PENDING_APPROVAL: { label: 'Chờ duyệt ban hành', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  PRE_PRESS_COMPLETED: { label: 'Chế bản xong', color: 'bg-purple-50 text-purple-600', dotColor: 'bg-purple-400' },
  READY_FOR_SCHEDULING: { label: 'Sẵn sàng lên KH', color: 'bg-sky-100 text-sky-700', dotColor: 'bg-sky-500' },
  MATERIALS_READY: { label: 'Vật tư sẵn sàng', color: 'bg-teal-100 text-teal-700', dotColor: 'bg-teal-500' },
  PLANNED: { label: 'Đã lên KH', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  RELEASED: { label: 'Đã phát hành', color: 'bg-cyan-100 text-cyan-700', dotColor: 'bg-cyan-500' },
  IN_PROGRESS: { label: 'Đang SX', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
  MANUFACTURED: { label: 'Đã sản xuất', color: 'bg-emerald-50 text-emerald-700', dotColor: 'bg-emerald-400' },
  COMPLETED: { label: 'Hoàn thành', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  CLOSED: { label: 'Đã đóng', color: 'bg-slate-100 text-slate-700', dotColor: 'bg-slate-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
  ON_HOLD: { label: 'Tạm dừng', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
  PAUSED: { label: 'Tạm ngưng', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
};

export const DeploymentModelRegistry: StatusMap = {
  INTERNAL_100: { label: 'Nội bộ 100%', color: 'bg-emerald-100 text-emerald-700' },
  OUTSOURCE_100: { label: 'Gia công 100%', color: 'bg-purple-100 text-purple-700' },
  HYBRID_PRINT_INTERNAL: { label: 'In nội bộ + GC', color: 'bg-blue-100 text-blue-700' },
  HYBRID_PRINT_EXTERNAL: { label: 'In gia công + NB', color: 'bg-cyan-100 text-cyan-700' },
  HYBRID_FINISH_EXTERNAL: { label: 'Thành phẩm gia công', color: 'bg-amber-100 text-amber-700' },
  TRANSPORT: { label: 'Vận chuyển', color: 'bg-slate-100 text-slate-600' },
};

export const RoutingStepStatusRegistry: StatusMap = {
  PLANNED: { label: 'Đã lên KH', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  READY: { label: 'Sẵn sàng', color: 'bg-teal-100 text-teal-700', dotColor: 'bg-teal-500' },
  SCHEDULED: { label: 'Đã xếp lịch', color: 'bg-cyan-100 text-cyan-700', dotColor: 'bg-cyan-500' },
  RUNNING: { label: 'Đang chạy', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
  COMPLETED: { label: 'Hoàn thành', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  PAUSED: { label: 'Tạm dừng', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const MaterialPlanningStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ xử lý', color: 'bg-amber-100 text-amber-700' },
  SUFFICIENT: { label: 'Đủ vật tư', color: 'bg-emerald-100 text-emerald-700' },
  PARTIAL: { label: 'Thiếu 1 phần', color: 'bg-orange-100 text-orange-700' },
  SHORTAGE: { label: 'Thiếu vật tư', color: 'bg-rose-100 text-rose-700' },
  ORDERED: { label: 'Đã đặt hàng', color: 'bg-blue-100 text-blue-700' },
  PARTIAL_RECEIVED: { label: 'Nhận 1 phần', color: 'bg-cyan-100 text-cyan-700' },
  RECEIVED: { label: 'Đã nhận đủ', color: 'bg-emerald-100 text-emerald-700' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700' },
};

// ══════════════════════════════════════════════
// QUALITY MODULE
// ══════════════════════════════════════════════

export const QCStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ kiểm', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  INSPECTING: { label: 'Đang kiểm', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  PASSED: { label: 'Đạt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  WARNING: { label: 'Cảnh báo', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  FAILED: { label: 'Không đạt', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
  REWORK: { label: 'Làm lại', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
  RE_INSPECT: { label: 'Kiểm lại', color: 'bg-purple-100 text-purple-700', dotColor: 'bg-purple-500' },
};

export const NCRSourceRegistry: StatusMap = {
  INTERNAL: { label: 'Nội bộ', color: 'bg-blue-100 text-blue-700' },
  CUSTOMER: { label: 'Khách hàng', color: 'bg-amber-100 text-amber-700' },
  SUPPLIER: { label: 'Nhà cung cấp', color: 'bg-purple-100 text-purple-700' },
};

export const NCRSeverityRegistry: StatusMap = {
  LOW: { label: 'Thấp', color: 'bg-slate-100 text-slate-600' },
  MEDIUM: { label: 'Trung bình', color: 'bg-amber-100 text-amber-700' },
  HIGH: { label: 'Cao', color: 'bg-orange-100 text-orange-700' },
  CRITICAL: { label: 'Nghiêm trọng', color: 'bg-rose-100 text-rose-700' },
};

export const NCRStatusRegistry: StatusMap = {
  OPEN: { label: 'Mở', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  INVESTIGATING: { label: 'Đang điều tra', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  CORRECTIVE_ACTION: { label: 'Hành động KP', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
  VERIFICATION: { label: 'Xác minh', color: 'bg-cyan-100 text-cyan-700', dotColor: 'bg-cyan-500' },
  PREVENTIVE_ACTION: { label: 'Phòng ngừa', color: 'bg-purple-100 text-purple-700', dotColor: 'bg-purple-500' },
  CLOSED: { label: 'Đã đóng', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

/** C-13: cách xử lý NCR — một nguồn nhãn cho form, chi tiết, danh sách, báo cáo */
export const NCRDispositionRegistry: StatusMap = {
  PENDING: { label: 'Chưa quyết định', color: 'bg-slate-100 text-slate-600' },
  REWORK: { label: 'Sửa lỗi / chọn lọc', color: 'bg-amber-100 text-amber-800' },
  SCRAP_REPRINT: { label: 'Hủy & sản xuất bù', color: 'bg-rose-100 text-rose-800' },
  CONCESSION: { label: 'Nhân nhượng', color: 'bg-blue-100 text-blue-800' },
  RETURN_VENDOR: { label: 'Trả nhà cung cấp', color: 'bg-purple-100 text-purple-800' },
  DOWNGRADE: { label: 'Hạ cấp / tận dụng', color: 'bg-slate-100 text-slate-800' },
};

// ══════════════════════════════════════════════
// OUTSOURCING MODULE
// ══════════════════════════════════════════════

export const OutsourcePOStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  PENDING: { label: 'Chờ duyệt', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  IN_PROGRESS: { label: 'Đang GC', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
  COMPLETED: { label: 'Hoàn thành', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const PaymentStatusRegistry: StatusMap = {
  UNPAID: { label: 'Chưa TT', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
  PARTIAL: { label: 'TT 1 phần', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  PAID: { label: 'Đã TT', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
};

// ══════════════════════════════════════════════
// WAREHOUSE MODULE
// ══════════════════════════════════════════════

export const InventoryStatusRegistry: StatusMap = {
  AVAILABLE: { label: 'Còn hàng', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  RESERVED: { label: 'Đã đặt', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  LOW_STOCK: { label: 'Sắp hết', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  OUT_OF_STOCK: { label: 'Hết hàng', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const TransactionTypeRegistry: StatusMap = {
  RECEIPT: { label: 'Nhập kho', color: 'bg-emerald-100 text-emerald-700' },
  ISSUE: { label: 'Xuất kho', color: 'bg-blue-100 text-blue-700' },
  RESERVATION: { label: 'Đặt trước', color: 'bg-amber-100 text-amber-700' },
  TRANSFER: { label: 'Chuyển kho', color: 'bg-cyan-100 text-cyan-700' },
  ADJUSTMENT: { label: 'Điều chỉnh', color: 'bg-purple-100 text-purple-700' },
};

// ══════════════════════════════════════════════
// SUPPLY MODULE
// ══════════════════════════════════════════════

export const PurchaseOrderStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  PENDING: { label: 'Chờ duyệt', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  APPROVED: { label: 'Đã duyệt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  ORDERED: { label: 'Đã đặt hàng', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  PARTIAL_RECEIVED: { label: 'Nhận 1 phần', color: 'bg-cyan-100 text-cyan-700', dotColor: 'bg-cyan-500' },
  RECEIVED: { label: 'Đã nhận đủ', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const PurchaseRequestStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  PENDING: { label: 'Chờ duyệt', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  APPROVED: { label: 'Đã duyệt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  REJECTED: { label: 'Từ chối', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
  ORDERED: { label: 'Đã đặt hàng', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
};

// ══════════════════════════════════════════════
// LOGISTICS MODULE
// ══════════════════════════════════════════════

export const DeliveryStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  SCHEDULED: { label: 'Đã lên lịch', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  IN_TRANSIT: { label: 'Đang giao', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  DELIVERED: { label: 'Đã giao', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  COMPLETED: { label: 'Hoàn tất chứng từ', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
  // Legacy values
  Pending: { label: 'Chờ giao', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  Processing: { label: 'Đang xử lý', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  Completed: { label: 'Đã hoàn tất', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
};

// ══════════════════════════════════════════════
// TECH / TOOLING MODULE
// ══════════════════════════════════════════════

export const PrepressStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ ghép bài', color: 'bg-slate-100 text-slate-800', dotColor: 'bg-slate-500' },
  LAYOUT_PROCESSING: { label: 'Đang bình file', color: 'bg-blue-100 text-blue-800', dotColor: 'bg-blue-500' },
  PLATE_OUTPUT: { label: 'Đang xuất kẽm CTP', color: 'bg-purple-100 text-purple-800', dotColor: 'bg-purple-500' },
  MOLD_ORDERED: { label: 'Đã đặt khuôn bế', color: 'bg-amber-100 text-amber-800', dotColor: 'bg-amber-500' },
  COMPLETED: { label: 'Hoàn thành bình bài', color: 'bg-emerald-100 text-emerald-800', dotColor: 'bg-emerald-500' },
  // Legacy
  IN_PROGRESS: { label: 'Đang xử lý', color: 'bg-blue-100 text-blue-800', dotColor: 'bg-blue-500' },
  NEEDS_REVIEW: { label: 'Cần duyệt', color: 'bg-amber-100 text-amber-800', dotColor: 'bg-amber-500' },
  APPROVED: { label: 'Đã duyệt', color: 'bg-indigo-100 text-indigo-800', dotColor: 'bg-indigo-500' },
  READY: { label: 'Hoàn thành', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' },
  ON_HOLD: { label: 'Tạm dừng', color: 'bg-rose-100 text-rose-800', dotColor: 'bg-rose-500' },
};

export const DesignStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ xử lý', color: 'bg-slate-100 text-slate-700', dotColor: 'bg-slate-400' },
  DESIGNING: { label: 'Đang thiết kế', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  REVIEWING: { label: 'Chờ duyệt KCS', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  APPROVED: { label: 'Đã phê duyệt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  REJECTED: { label: 'Từ chối / Sửa lại', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const ToolingStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ nghiệm thu', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  AVAILABLE: { label: 'Sẵn sàng trên kệ', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  IN_USE: { label: 'Đang chạy máy', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  MAINTENANCE: { label: 'Đang bảo dưỡng', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
  DAMAGED: { label: 'Hỏng / Sự cố', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
  DISCARDED: { label: 'Đã thanh lý / Hủy', color: 'bg-zinc-200 text-zinc-700', dotColor: 'bg-zinc-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-stone-100 text-stone-600', dotColor: 'bg-stone-400' },
  NEW: { label: 'Làm mới', color: 'bg-blue-100 text-blue-800', dotColor: 'bg-blue-500' },
  REUSE: { label: 'Dùng lại', color: 'bg-emerald-100 text-emerald-800', dotColor: 'bg-emerald-500' },
  REUSE_EDITED: { label: 'Sửa đổi', color: 'bg-amber-100 text-amber-800', dotColor: 'bg-amber-500' },
  IN_PRODUCTION: { label: 'Đang làm khuôn', color: 'bg-purple-100 text-purple-800', dotColor: 'bg-purple-500' },
  READY: { label: 'Sẵn sàng', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' },
  ON_HOLD: { label: 'Tạm dừng', color: 'bg-rose-100 text-rose-800', dotColor: 'bg-rose-500' },
  // Legacy lowercase
  pending: { label: 'Chờ nhập', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  active: { label: 'Sẵn sàng', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  maintenance: { label: 'Bảo trì', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
  retired: { label: 'Đã hủy', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
};

export const PlateStatusRegistry = ToolingStatusRegistry;

export const ReadinessStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  READY: { label: 'Sẵn sàng', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  IN_PROGRESS: { label: 'Đang làm', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  COMPLETED: { label: 'Hoàn thành', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
  PARTIAL: { label: 'Một phần', color: 'bg-orange-100 text-orange-700', dotColor: 'bg-orange-500' },
  // Legacy lowercase
  pending: { label: 'Chờ', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  ready: { label: 'Sẵn sàng', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  in_progress: { label: 'Đang làm', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  completed: { label: 'Hoàn thành', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-500' },
};

export const ApprovalStatusRegistry: StatusMap = {
  APPROVED: { label: 'Đã duyệt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  PENDING: { label: 'Chờ duyệt', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  REJECTED: { label: 'Từ chối', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
  OBSOLETE: { label: 'Lưu trữ / Lỗi thời', color: 'bg-zinc-100 text-zinc-700', dotColor: 'bg-zinc-500' },
  // Legacy lowercase
  approved: { label: 'Đã duyệt', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  draft: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  pending: { label: 'Chờ duyệt', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  rejected: { label: 'Từ chối', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const WIPTransferStatusRegistry: StatusMap = {
  pending: { label: 'Chờ xử lý', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  completed: { label: 'Hoàn thành', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  cancelled: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

// ══════════════════════════════════════════════
// FINANCE MODULE
// ══════════════════════════════════════════════

export const ReceiptStatusRegistry: StatusMap = {
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  CONFIRMED: { label: 'Đã xác nhận', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  COMPLETED: { label: 'Hoàn thành', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  CANCELLED: { label: 'Đã hủy', color: 'bg-rose-100 text-rose-700', dotColor: 'bg-rose-500' },
};

export const ARReconciliationStatusRegistry: StatusMap = {
  PENDING: { label: 'Chờ xác nhận', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  CONFIRMED: { label: 'Đã xác nhận', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  RECONCILED: { label: 'Đã đối soát', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
};

export const QCTemplateStatusRegistry: StatusMap = {
  ACTIVE: { label: 'Áp dụng', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  DRAFT: { label: 'Bản nháp', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  ARCHIVED: { label: 'Lưu trữ', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
};

export const FeedbackStatusRegistry: StatusMap = {
  OPEN: { label: 'Mở', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  IN_PROGRESS: { label: 'Đang xử lý', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  RESOLVED: { label: 'Đã giải quyết', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  CLOSED: { label: 'Đã đóng', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
};

// ══════════════════════════════════════════════
// MASTER REGISTRY -- Lookup by enum name
// ══════════════════════════════════════════════

export const STATUS_REGISTRY = {
  UserStatus: UserStatusRegistry,
  NotificationType: NotificationTypeRegistry,
  MachineType: MachineTypeRegistry,
  MachineStatus: MachineStatusRegistry,
  MachineLogType: MachineLogTypeRegistry,
  MaterialCategory: MaterialCategoryRegistry,
  QuotationStatus: QuotationStatusRegistry,
  OrderStatus: OrderStatusRegistry,
  OrderDetailStatus: OrderDetailStatusRegistry,
  ProductionPriority: ProductionPriorityRegistry,
  ProductionOrderStatus: ProductionOrderStatusRegistry,
  DeploymentModel: DeploymentModelRegistry,
  RoutingStepStatus: RoutingStepStatusRegistry,
  MaterialPlanningStatus: MaterialPlanningStatusRegistry,
  QCStatus: QCStatusRegistry,
  NCRSource: NCRSourceRegistry,
  NCRSeverity: NCRSeverityRegistry,
  NCRStatus: NCRStatusRegistry,
  NCRDisposition: NCRDispositionRegistry,
  OutsourcePOStatus: OutsourcePOStatusRegistry,
  PaymentStatus: PaymentStatusRegistry,
  InventoryStatus: InventoryStatusRegistry,
  TransactionType: TransactionTypeRegistry,
  PurchaseOrderStatus: PurchaseOrderStatusRegistry,
  PurchaseRequestStatus: PurchaseRequestStatusRegistry,
  DeliveryStatus: DeliveryStatusRegistry,
  PrepressStatus: PrepressStatusRegistry,
  DesignStatus: DesignStatusRegistry,
  ToolingStatus: ToolingStatusRegistry,
  PlateStatus: PlateStatusRegistry,
  OrderPriority: OrderPriorityRegistry,
  ReadinessStatus: ReadinessStatusRegistry,
  ApprovalStatus: ApprovalStatusRegistry,
  WIPTransferStatus: WIPTransferStatusRegistry,
  ReceiptStatus: ReceiptStatusRegistry,
  ARReconciliationStatus: ARReconciliationStatusRegistry,
  QCTemplateStatus: QCTemplateStatusRegistry,
  FeedbackStatus: FeedbackStatusRegistry,
} as const;

export type StatusRegistryKey = keyof typeof STATUS_REGISTRY;

// ══════════════════════════════════════════════
// UTILITY FUNCTIONS
// ══════════════════════════════════════════════

/** Lấy config cho 1 status value */
export function getStatusConfig(enumName: StatusRegistryKey, value: string): StatusConfig {
  const map = STATUS_REGISTRY[enumName] as StatusMap;
  return map?.[value] || { label: value, color: 'bg-slate-100 text-slate-600' };
}

/** Lấy label tiếng Việt */
export function getStatusLabel(enumName: StatusRegistryKey, value: string): string {
  return getStatusConfig(enumName, value).label;
}

/** Tìm label tiếng Việt cho status code bất kỳ -- tìm qua TẤT CẢ registry */
export function tryResolveStatusLabel(code: string): string {
  if (!code) return code;
  for (const map of Object.values(STATUS_REGISTRY)) {
    const entry = (map as StatusMap)[code];
    if (entry?.label) return entry.label;
  }
  return code;
}

/** Lấy danh sách options cho Select dropdown */
export function getStatusOptions(
  enumName: StatusRegistryKey,
  includeAll: boolean = true,
): Array<{ value: string; label: string }> {
  const map = STATUS_REGISTRY[enumName] as StatusMap;
  const options = Object.entries(map).map(([value, cfg]) => ({
    value,
    label: cfg.label,
  }));
  return includeAll
    ? [{ value: 'all', label: 'Tất cả' }, ...options]
    : options;
}
